import { useState, useEffect, useCallback } from 'react'
import { getContent, updateContent as apiUpdateContent } from '@/lib/api'
import { getLocalDraft, setLocalDraft, removeLocalDraft } from '@/lib/storage'
import type { ContentData } from '@/types'
import { useDebounce } from './useDebounce'

interface UseContentOptions {
  autosaveDelay?: number
  loadDraft?: boolean
}

interface UseContentReturn {
  content: ContentData | null
  isLoading: boolean
  error: string | null
  isDirty: boolean
  lastSaved: string | null
  updateField: (key: string, value: string) => void
  saveDraft: () => Promise<void>
  publish: () => Promise<void>
  discardChanges: () => void
}

export function useContent(
  sectionKey: string,
  options: UseContentOptions = {}
): UseContentReturn {
  const { autosaveDelay = 2000, loadDraft = true } = options

  const [content, setContent] = useState<ContentData | null>(null)
  const [originalContent, setOriginalContent] = useState<ContentData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [lastSaved, setLastSaved] = useState<string | null>(null)

  // Debounced content for autosave
  const debouncedContent = useDebounce(content, autosaveDelay)

  // Load content on mount
  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Try to load local draft first
        if (loadDraft) {
          const localDraft = getLocalDraft<ContentData>(sectionKey)
          if (localDraft) {
            setContent(localDraft)
            setIsDirty(true)
          }
        }

        // Fetch from API
        const response = await getContent(sectionKey)
        setOriginalContent(response.content)
        setLastSaved(response.updatedAt)

        // Only set content if no local draft
        if (!loadDraft || !getLocalDraft(sectionKey)) {
          setContent(response.content)
        }
      } catch (err) {
        if (err instanceof Error && err.message.includes('404')) {
          // Section doesn't exist yet, start with empty content
          setContent({})
          setOriginalContent({})
        } else {
          setError(err instanceof Error ? err.message : 'Erreur de chargement')
        }
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [sectionKey, loadDraft])

  // Autosave to localStorage
  useEffect(() => {
    if (isDirty && debouncedContent) {
      setLocalDraft(sectionKey, debouncedContent)
    }
  }, [debouncedContent, isDirty, sectionKey])

  const updateField = useCallback((key: string, value: string) => {
    setContent((prev) => {
      const updated = { ...prev, [key]: value }
      return updated
    })
    setIsDirty(true)
  }, [])

  const saveDraft = useCallback(async () => {
    if (!content) return

    try {
      const response = await apiUpdateContent(sectionKey, {
        content,
        publish: false,
      })
      setLastSaved(response.updatedAt)
      // Keep local draft for preview purposes
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de sauvegarde')
      throw err
    }
  }, [content, sectionKey])

  const publish = useCallback(async () => {
    if (!content) return

    try {
      const response = await apiUpdateContent(sectionKey, {
        content,
        publish: true,
      })
      setOriginalContent(content)
      setLastSaved(response.updatedAt)
      setIsDirty(false)
      removeLocalDraft(sectionKey)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de publication')
      throw err
    }
  }, [content, sectionKey])

  const discardChanges = useCallback(() => {
    setContent(originalContent)
    setIsDirty(false)
    removeLocalDraft(sectionKey)
  }, [originalContent, sectionKey])

  return {
    content,
    isLoading,
    error,
    isDirty,
    lastSaved,
    updateField,
    saveDraft,
    publish,
    discardChanges,
  }
}
